import { ChevronLeft } from "lucide-react";
import { useTranslation } from "react-i18next";

import { hereticAppUrl } from "../../lib/heretic.js";
import { useTelegramWebApp } from "../../hooks/useTelegramWebApp.js";
import { useDraftsStore } from "../../stores/drafts-store.js";

import { CrossPromoCards } from "../cross-promo/CrossPromoCards.js";

interface SectionProps {
  title: string;
  body: string;
}

function Section({ title, body }: SectionProps) {
  return (
    <section className="border-b border-[var(--tg-secondary-bg)] px-3 py-3">
      <h2 className="mb-1 text-sm font-semibold text-[var(--tg-text)]">
        {title}
      </h2>
      <p className="text-xs leading-relaxed text-[var(--tg-hint)]">{body}</p>
    </section>
  );
}

export function HelpScreen() {
  const { t } = useTranslation();
  const setScreen = useDraftsStore((s) => s.setScreen);
  const wa = useTelegramWebApp();

  function openDesktop() {
    const url = hereticAppUrl();
    if (wa?.openLink) wa.openLink(url);
    else window.open(url, "_blank", "noopener,noreferrer");
  }

  const sectionIds = [
    "intro",
    "formatting",
    "share",
    "channel_admin",
    "drafts",
    "limits",
    "media",
    "desktop",
  ] as const;

  return (
    <div className="flex h-full flex-col bg-[var(--tg-bg)]">
      <header className="flex items-center gap-2 border-b border-[var(--tg-secondary-bg)] px-2 py-2">
        <button
          type="button"
          aria-label={t("help.back")}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-[var(--tg-secondary-bg)]"
          onClick={() => {
            setScreen("editor");
          }}
        >
          <ChevronLeft size={18} />
        </button>
        <h1 className="text-sm font-semibold tracking-tight text-[var(--tg-text)]">
          {t("help.title")}
        </h1>
      </header>
      <div className="flex-1 overflow-y-auto">
        {sectionIds.map((id) => (
          <Section
            key={id}
            title={t(`help.sections.${id}.title`)}
            body={t(`help.sections.${id}.body`)}
          />
        ))}
        <div className="px-3 py-4">
          <button
            type="button"
            onClick={openDesktop}
            className="w-full rounded-lg bg-[var(--tg-link)] px-3 py-2 text-sm font-medium text-white"
          >
            {t("help.desktop_cta")}
          </button>
        </div>

        <div className="border-t border-[var(--tg-secondary-bg)] px-3 pb-6 pt-4">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-[var(--tg-hint)]">
            {t("help.other_tools")}
          </h2>
          <CrossPromoCards />
        </div>
      </div>
    </div>
  );
}
